import { useCallback, useEffect, useLayoutEffect, useRef } from 'react'
import { botApi } from '@/bot/api'
import type { WorkbenchSavedStateV2 } from './workbenchState'

const SAVE_DEBOUNCE_MS = 1500
const BACKSTOP_INTERVAL_MS = 10000

/**
 * Debounced auto-persistence of the workbench chart state (usePlayback recipe:
 * debounce + interval backstop + unmount flush + dedup against the last saved
 * payload). Call `markDirty()` from every mutation path; the hook serializes
 * lazily via `getState` when a save actually fires, so rapid edits coalesce.
 *
 * `enabled` stays false until the mount-time restore has fully applied — that
 * both blocks premature saves and inertizes the restore's own setState storm.
 */
export function useWorkbenchAutosave({
  bid,
  enabled,
  getState,
}: {
  bid: string
  enabled: boolean
  getState: () => WorkbenchSavedStateV2 | null
}): { markDirty: () => void } {
  const enabledRef = useRef(enabled)
  const getStateRef = useRef(getState)
  useLayoutEffect(() => {
    enabledRef.current = enabled
  }, [enabled])
  useLayoutEffect(() => {
    getStateRef.current = getState
  }, [getState])

  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const lastSavedRef = useRef<string | null>(null)

  const saveIfChanged = useCallback(
    (onUnload = false) => {
      if (!enabledRef.current) return
      const state = getStateRef.current()
      if (!state) return
      const json = JSON.stringify(state)
      if (json === lastSavedRef.current) return
      const previous = lastSavedRef.current
      lastSavedRef.current = json
      if (onUnload) {
        botApi.saveWorkbenchOnUnload(bid, state as unknown as Record<string, unknown>)
        return
      }
      botApi.saveWorkbench(bid, state as unknown as Record<string, unknown>).catch((err) => {
        // Roll back the dedup marker so the interval backstop retries.
        lastSavedRef.current = previous
        if (import.meta.env.DEV) console.error('Workbench autosave failed:', err)
      })
    },
    [bid],
  )

  const markDirty = useCallback(() => {
    if (!enabledRef.current) return
    if (timerRef.current) clearTimeout(timerRef.current)
    timerRef.current = setTimeout(() => {
      timerRef.current = null
      saveIfChanged()
    }, SAVE_DEBOUNCE_MS)
  }, [saveIfChanged])

  // Seed the dedup marker when autosave turns on, so a restore that applied
  // cleanly doesn't immediately re-save the identical state.
  useEffect(() => {
    if (!enabled) return
    const state = getStateRef.current()
    lastSavedRef.current = state ? JSON.stringify(state) : null
  }, [enabled])

  // Interval backstop: catches any mutation path that missed markDirty and
  // retries failed saves. Cheap — serialize + string compare when idle.
  useEffect(() => {
    const interval = setInterval(() => saveIfChanged(), BACKSTOP_INTERVAL_MS)
    return () => clearInterval(interval)
  }, [saveIfChanged])

  // Flush on unmount (tab switches unmount the whole Workbench).
  useEffect(() => {
    return () => {
      if (timerRef.current) {
        clearTimeout(timerRef.current)
        timerRef.current = null
      }
      saveIfChanged()
    }
  }, [saveIfChanged])

  // Best-effort flush when the page itself goes away.
  useEffect(() => {
    const onPageHide = () => {
      if (timerRef.current) {
        clearTimeout(timerRef.current)
        timerRef.current = null
      }
      saveIfChanged(true)
    }
    window.addEventListener('pagehide', onPageHide)
    return () => window.removeEventListener('pagehide', onPageHide)
  }, [saveIfChanged])

  return { markDirty }
}
